import React, { useState, useEffect } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/WholesaleApplication.module.css';

const WholesaleApplication = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [formData, setFormData] = useState({
    businessName: '',
    address: '',
    streetAddressLine2: '',
    city: '',
    commercialRegistration: '',
    establishmentId: '',
    poBox: '',
    purchasePhone: '',
    purchaseEmail: '',
    businessOwnerQIDNumber: '',
    businessOwnerName: '',
    applicantQIDNumber: '',
    applicantName: '',
    creditLimit: '',
    creditPeriod: '30 days',
    paymentMode: '',
    businessType: ''
  });

  const [files, setFiles] = useState({
    crDocument: null,
    ownerQID: null,
    computerCard: null,
    guaranteeCheque: null
  });

  const [uploadProgress, setUploadProgress] = useState({
    crDocument: 0,
    ownerQID: 0,
    computerCard: 0,
    guaranteeCheque: 0
  });

  useEffect(() => {
    // Add class to body to remove global gradient
    document.body.classList.add('wholesale-body');
    document.body.style.padding = '0';
    document.body.style.margin = '0';

    return () => {
      // Cleanup
      document.body.classList.remove('wholesale-body');
      document.body.style.padding = '';
      document.body.style.margin = '';
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateFile = (file) => {
    const maxSizeInMB = 10;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    
    if (!file) return { isValid: false, error: 'Please select a file' };
    
    if (file.type !== 'application/pdf') {
      return { isValid: false, error: 'Only PDF files are allowed' };
    }
    
    if (file.size > maxSizeInBytes) {
      return { isValid: false, error: `File size must be less than ${maxSizeInMB}MB` };
    }
    
    return { isValid: true, error: null };
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    
    if (!file) {
      setFiles(prev => ({ ...prev, [fileType]: null }));
      return;
    }

    const validation = validateFile(file);
    
    if (!validation.isValid) {
      setError(validation.error);
      e.target.value = '';
      return;
    }

    setFiles(prev => ({ ...prev, [fileType]: file }));
    setError(null);
  };


  const uploadFileToStorage = async (file, path) => {
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytes(storageRef, file);
    
    try {
      const snapshot = await uploadTask;
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validate required fields
      const requiredFields = [
        'businessName', 'address', 'city', 'commercialRegistration', 'establishmentId', 
        'poBox', 'purchasePhone', 'purchaseEmail', 'businessOwnerQIDNumber', 
        'businessOwnerName', 'applicantQIDNumber', 'applicantName', 'creditLimit', 
        'creditPeriod', 'paymentMode', 'businessType'
      ];
      
      const missingFields = requiredFields.filter(field => !formData[field] || formData[field].trim() === '');
      
      if (missingFields.length > 0) {
        setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      // Validate required documents
      const requiredDocuments = ['crDocument', 'ownerQID', 'computerCard', 'guaranteeCheque'];
      const missingDocuments = requiredDocuments.filter(docType => !files[docType]);
      
      if (missingDocuments.length > 0) {
        const documentLabels = {
          crDocument: 'Commercial Registration (CR)',
          ownerQID: 'Owner QID',
          computerCard: 'Computer Card',
          guaranteeCheque: 'Guarantee Cheque'
        };
        const missingLabels = missingDocuments.map(doc => documentLabels[doc]);
        setError(`Please upload all required documents: ${missingLabels.join(', ')}`);
        return;
      }

      // Validate privacy policy acceptance
      if (!privacyAccepted) {
        setError('Please accept the Privacy Policy to continue');
        return;
      }

      // Validate terms and conditions acceptance
      if (!termsAccepted) {
        setError('Please accept the Terms and Conditions to continue');
        return;
      }

      // Upload documents to Firebase Storage
      setUploadStatus('Uploading documents...');
      const timestamp = Date.now();
      const businessName = formData.businessName.replace(/[^a-zA-Z0-9]/g, '_');
      
      const documentUrls = {};
      const fileEntries = Object.entries(files).filter(([_, file]) => file);
      
      for (let i = 0; i < fileEntries.length; i++) {
        const [docType, file] = fileEntries[i];
        const filePath = `wholesale-documents/${businessName}_${timestamp}/${docType}.pdf`;
        setUploadStatus(`Uploading document ${i + 1} of ${fileEntries.length}...`);
        documentUrls[docType] = await uploadFileToStorage(file, filePath);
      }
      
      setUploadStatus('Saving application...');
      
      // Map form data to match admin panel expectations
      const customerData = {
        // Core fields the admin expects
        businessName: formData.businessName,
        contactName: formData.applicantName, // Use applicant name as main contact
        email: formData.purchaseEmail, // Use purchase email as main email
        phone: formData.purchasePhone,
        address: `${formData.address}${formData.streetAddressLine2 ? ', ' + formData.streetAddressLine2 : ''}, ${formData.city}`,
        taxId: formData.commercialRegistration,
        
        // Additional fields from the new form
        streetAddress: formData.address,
        streetAddressLine2: formData.streetAddressLine2,
        city: formData.city,
        commercialRegistration: formData.commercialRegistration,
        establishmentId: formData.establishmentId,
        poBox: formData.poBox,
        purchasePhone: formData.purchasePhone,
        purchaseEmail: formData.purchaseEmail,
        businessOwnerQIDNumber: formData.businessOwnerQIDNumber,
        businessOwnerName: formData.businessOwnerName,
        applicantQIDNumber: formData.applicantQIDNumber,
        applicantName: formData.applicantName,
        creditLimit: formData.creditLimit,
        creditPeriod: formData.creditPeriod,
        paymentMode: formData.paymentMode,
        businessType: formData.businessType,
        
        // Document URLs
        documents: documentUrls,
        
        // Privacy and Consent
        privacyPolicyAccepted: true,
        privacyConsentDate: new Date(),
        pdplCompliance: true,
        termsAndConditionsAccepted: true,
        termsAcceptanceDate: new Date(),
        
        // System fields
        status: 'pending',
        createdAt: new Date()
      };

      console.log('Submitting wholesale application with data:', customerData);
      
      const docRef = await addDoc(collection(db, 'wholesaleCustomers'), customerData);
      console.log('Application submitted successfully with ID:', docRef.id);
      
      setUploadStatus('');
      setSuccess('Your wholesale application has been submitted successfully. We will review it and get back to you soon.');
    } catch (error) {
      console.error('Error submitting application:', error);
      console.error('Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // More specific error messages
      if (error.code === 'permission-denied') {
        setError('Permission denied. Please check your internet connection and try again.');
      } else if (error.code === 'unavailable') {
        setError('Service temporarily unavailable. Please try again in a moment.');
      } else if (error.message.includes('network')) {
        setError('Network error. Please check your internet connection and try again.');
      } else {
        setError(`Failed to submit application: ${error.message}`);
      }
    } finally {
      setLoading(false);
      setUploadStatus('');
    }
  };

  if (success) {
    return (
      <div className={styles.container}>
        <div className={styles.successCard}>
          <div className={styles.successIcon}>✓</div>
          <h1 className={styles.successTitle}>Application Submitted</h1>
          <p className={styles.successMessage}>{success}</p>
          <p className={styles.redirectMessage}>Thank you for your interest in Grey Specialty Coffee wholesale services.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.formCard}>
        <div className={styles.header}>
          <img src="/images/logo.png" alt="Grey Official Logo" className={styles.logo} />
          <h1 className={styles.title}>Wholesale Account Application</h1>
          <p className={styles.subtitle}>Please provide all required details to register your business with Grey</p>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Business Name */}
          <div className={styles.formGroup}>
            <label htmlFor="businessName" className={styles.label}>
              Business Name*
            </label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleInputChange}
              className={styles.input}
              required
              disabled={loading}
            />
          </div>


          {/* Address */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Address*</label>
            <input
              type="text"
              name="address"
              placeholder="Street Address"
              value={formData.address}
              onChange={handleInputChange}
              className={styles.input}
              required
              disabled={loading}
            />
            <input
              type="text"
              name="streetAddressLine2"
              placeholder="Street Address Line 2"
              value={formData.streetAddressLine2}
              onChange={handleInputChange}
              className={styles.input}
              disabled={loading}
              style={{ marginTop: '8px' }}
            />
            <input
              type="text"
              name="city"
              placeholder="City"
              value={formData.city}
              onChange={handleInputChange}
              className={styles.input}
              required
              disabled={loading}
              style={{ marginTop: '8px' }}
            />
          </div>

          {/* Commercial Registration & Establishment ID */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="commercialRegistration" className={styles.label}>
                Commercial Registration (CR) #*
              </label>
              <input
                type="text"
                id="commercialRegistration"
                name="commercialRegistration"
                value={formData.commercialRegistration}
                onChange={handleInputChange}
                className={styles.input}
                required
                disabled={loading}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="establishmentId" className={styles.label}>
                Establishment ID*
              </label>
              <input
                type="text"
                id="establishmentId"
                name="establishmentId"
                placeholder="e.g., 23"
                value={formData.establishmentId}
                onChange={handleInputChange}
                className={styles.input}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* PO Box */}
          <div className={styles.formGroup}>
            <label htmlFor="poBox" className={styles.label}>
              PO Box*
            </label>
            <input
              type="text"
              id="poBox"
              name="poBox"
              placeholder="e.g., 23"
              value={formData.poBox}
              onChange={handleInputChange}
              className={styles.input}
              required
              disabled={loading}
            />
          </div>

          {/* Purchase Department */}
          <div className={styles.formGroup}>
            <label htmlFor="purchasePhone" className={styles.label}>
              Purchase department Phone Number*
            </label>
            <input
              type="tel"
              id="purchasePhone"
              name="purchasePhone"
              placeholder="00000000"
              value={formData.purchasePhone}
              onChange={handleInputChange}
              className={styles.input}
              required
              disabled={loading}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="purchaseEmail" className={styles.label}>
              Purchase department E-mail*
            </label>
            <input
              type="email"
              id="purchaseEmail"
              name="purchaseEmail"
              placeholder="example@example.com"
              value={formData.purchaseEmail}
              onChange={handleInputChange}
              className={styles.input}
              required
              disabled={loading}
            />
          </div>

          {/* Business Owner Information */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="businessOwnerQIDNumber" className={styles.label}>
                Business Owner QID Number*
              </label>
              <input
                type="text"
                id="businessOwnerQIDNumber"
                name="businessOwnerQIDNumber"
                placeholder="QID Number"
                value={formData.businessOwnerQIDNumber}
                onChange={handleInputChange}
                className={styles.input}
                required
                disabled={loading}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="businessOwnerName" className={styles.label}>
                Business Owner Name*
              </label>
              <input
                type="text"
                id="businessOwnerName"
                name="businessOwnerName"
                placeholder="Full Name"
                value={formData.businessOwnerName}
                onChange={handleInputChange}
                className={styles.input}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Applicant Information */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="applicantQIDNumber" className={styles.label}>
                Applicant QID Number*
              </label>
              <input
                type="text"
                id="applicantQIDNumber"
                name="applicantQIDNumber"
                placeholder="QID Number"
                value={formData.applicantQIDNumber}
                onChange={handleInputChange}
                className={styles.input}
                required
                disabled={loading}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="applicantName" className={styles.label}>
                Applicant Name*
              </label>
              <input
                type="text"
                id="applicantName"
                name="applicantName"
                placeholder="Full Name"
                value={formData.applicantName}
                onChange={handleInputChange}
                className={styles.input}
                required
                disabled={loading}
              />
            </div>
          </div>


          {/* Credit Limit & Period */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="creditLimit" className={styles.label}>
                Credit Limit*
              </label>
              <input
                type="text"
                id="creditLimit"
                name="creditLimit"
                placeholder="e.g., 5000"
                value={formData.creditLimit}
                onChange={handleInputChange}
                className={styles.input}
                required
                disabled={loading}
              />
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="creditPeriod" className={styles.label}>
                Credit Period*
              </label>
              <input
                type="text"
                id="creditPeriod"
                name="creditPeriod"
                value="30 days"
                className={styles.input}
                disabled
                readOnly
              />
            </div>
          </div>

          {/* Payment Mode & Business Type */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="paymentMode" className={styles.label}>
                Payment Mode*
              </label>
              <select
                id="paymentMode"
                name="paymentMode"
                value={formData.paymentMode}
                onChange={handleInputChange}
                className={styles.select}
                required
                disabled={loading}
              >
                <option value="">Please Select</option>
                <option value="cheque">Cheque</option>
                <option value="bankTransfer">Bank Transfer</option>
                <option value="cash">Cash</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label htmlFor="businessType" className={styles.label}>
                Type of Business*
              </label>
              <select
                id="businessType"
                name="businessType"
                value={formData.businessType}
                onChange={handleInputChange}
                className={styles.select}
                required
                disabled={loading}
              >
                <option value="">Please Select</option>
                <option value="cafe">Cafe</option>
                <option value="restaurant">Restaurant</option>
                <option value="hotel">Hotel</option>
                <option value="office">Office</option>
                <option value="retail">Retail</option>
                <option value="others">Others</option>
              </select>
            </div>
          </div>

          {/* Document Upload Section */}
          <div className={styles.documentsSection}>
            <h3 className={styles.sectionTitle}>Required Documents</h3>
            <p className={styles.sectionSubtitle}>Please upload all documents in PDF format (max 10MB each)</p>
            
            {/* Commercial Registration */}
            <div className={styles.formGroup}>
              <label htmlFor="crDocument" className={styles.label}>
                Commercial Registration (CR) Document*
              </label>
              <div className={styles.fileInputWrapper}>
                <input
                  type="file"
                  id="crDocument"
                  accept=".pdf"
                  onChange={(e) => handleFileChange(e, 'crDocument')}
                  className={styles.fileInput}
                  disabled={loading}
                />
                <label htmlFor="crDocument" className={styles.fileLabel}>
                  {files.crDocument ? files.crDocument.name : 'Choose file'}
                </label>
              </div>
            </div>

            {/* Owner QID */}
            <div className={styles.formGroup}>
              <label htmlFor="ownerQID" className={styles.label}>
                Owner QID Document*
              </label>
              <div className={styles.fileInputWrapper}>
                <input
                  type="file"
                  id="ownerQID"
                  accept=".pdf"
                  onChange={(e) => handleFileChange(e, 'ownerQID')}
                  className={styles.fileInput}
                  disabled={loading}
                />
                <label htmlFor="ownerQID" className={styles.fileLabel}>
                  {files.ownerQID ? files.ownerQID.name : 'Choose file'}
                </label>
              </div>
            </div>

            {/* Computer Card */}
            <div className={styles.formGroup}>
              <label htmlFor="computerCard" className={styles.label}>
                Computer Card Document*
              </label>
              <div className={styles.fileInputWrapper}>
                <input
                  type="file"
                  id="computerCard"
                  accept=".pdf"
                  onChange={(e) => handleFileChange(e, 'computerCard')}
                  className={styles.fileInput}
                  disabled={loading}
                />
                <label htmlFor="computerCard" className={styles.fileLabel}>
                  {files.computerCard ? files.computerCard.name : 'Choose file'}
                </label>
              </div>
            </div>

            {/* Guarantee Cheque */}
            <div className={styles.formGroup}>
              <label htmlFor="guaranteeCheque" className={styles.label}>
                Guarantee Cheque*
              </label>
              <div className={styles.fileInputWrapper}>
                <input
                  type="file"
                  id="guaranteeCheque"
                  accept=".pdf"
                  onChange={(e) => handleFileChange(e, 'guaranteeCheque')}
                  className={styles.fileInput}
                  disabled={loading}
                />
                <label htmlFor="guaranteeCheque" className={styles.fileLabel}>
                  {files.guaranteeCheque ? files.guaranteeCheque.name : 'Choose file'}
                </label>
              </div>
            </div>
          </div>

          {/* Terms and Conditions Agreement */}
          <div className={styles.termsSection}>
            <h3 className={styles.termsTitle}>Terms and Conditions</h3>
            <div className={styles.termsContent}>
              <p><strong>By agreeing to these terms, the applicant undertakes the following:</strong></p>
              
              <ol className={styles.termsList}>
                <li>The applicant hereby undertakes to pay the full value of the goods supplied and billed by Grey Specialty Coffee on credit terms of sale (whatever the amount is whether within the credit limit granted or beyond the limit). Such payment shall be either in cash or by postdated cheque within 30 days from the date of delivery of the goods.</li>
                
                <li>The business owner is responsible to pay Grey Specialty Coffee for the full value of the goods supplied to the applicant irrespective of who ordered or received the goods on their behalf.</li>
                
                <li>In the event that the applicant fails to pay for the value of the goods received from Grey Specialty Coffee within the period specified in article (1) above, Grey Specialty Coffee shall be entitled without notice to withdraw all the goods supplied by them which are still in the possession of the applicant and furthermore to exercise such other actions that would enable Grey Specialty Coffee to fully recover all its outstanding dues from the applicant.</li>
                
                <li>Goods are considered sold once the receiver signs the delivery invoice. Items must be inspected before signing received. The sold items will not be taken back except under the following circumstances:
                  <ul className={styles.subList}>
                    <li><strong>A.</strong> If it is shown to be defective in respect of quality to be informed within (3) three days from the date of invoice</li>
                    <li><strong>B.</strong> Opened products must be returned within (3) days from the invoice date, presented with valid quality defect</li>
                    <li><strong>C.</strong> If the original invoice is presented at the time of return and all items are complete and in original condition within (7) seven days</li>
                  </ul>
                </li>
                
                <li>Payments will not be withheld for reason of any non-discounted invoice: (all disputed items will be communicated in writing before due date of payment)</li>
                
                <li>The credit offered by Grey Specialty Coffee must not exceed the agreed period of 30 days</li>
                
                <li>Only once the application has been signed by the customer (applicant) and thereafter approved by Grey Specialty Coffee will it be considered as a binding and irrevocable contract for both of the concerned parties.</li>
              </ol>
            </div>
            
            <div className={styles.termsCheckbox}>
              <input
                type="checkbox"
                id="termsAccepted"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className={styles.checkbox}
                disabled={loading}
              />
              <label htmlFor="termsAccepted" className={styles.termsLabel}>
                I have read, understood, and agree to all the Terms and Conditions stated above.*
              </label>
            </div>
          </div>

          {/* Privacy Policy Agreement */}
          <div className={styles.privacySection}>
            <h3 className={styles.privacyTitle}>Privacy Policy</h3>
            <div className={styles.privacyContent}>
              <p><strong>Grey Specialty Coffee - Privacy Policy Summary</strong></p>
              <p><em>In accordance with Qatar's Personal Data Protection Law No. 13 of 2016</em></p>
              
              <div className={styles.privacyText}>
                <h4>1. Data Controller</h4>
                <p>Grey Specialty Coffee is the data controller for your personal information. We are committed to protecting your privacy and handling your data responsibly.</p>
                
                <h4>2. Data We Collect</h4>
                <ul>
                  <li>Business information (name, address, commercial registration)</li>
                  <li>Contact details (phone, email, QID numbers)</li>
                  <li>Financial information (credit limits, payment preferences)</li>
                  <li>Document uploads (CR, QID, computer card, guarantee cheque)</li>
                  <li>Order and transaction history</li>
                </ul>
                
                <h4>3. How We Use Your Data</h4>
                <ul>
                  <li>Processing wholesale applications and managing accounts</li>
                  <li>Fulfilling orders and providing customer service</li>
                  <li>Compliance with legal and regulatory requirements</li>
                  <li>Fraud prevention and security purposes</li>
                  <li>Business communications and service improvements</li>
                </ul>
                
                <h4>4. Your Rights Under PDPL</h4>
                <ul>
                  <li><strong>Right to Access:</strong> Request copies of your personal data</li>
                  <li><strong>Right to Correction:</strong> Update inaccurate or incomplete information</li>
                  <li><strong>Right to Erasure:</strong> Request deletion when no longer necessary</li>
                  <li><strong>Right to Object:</strong> Withdraw consent for certain processing</li>
                  <li><strong>Right to Information:</strong> Understand how your data is used</li>
                </ul>
                
                <h4>5. Data Security</h4>
                <p>We implement comprehensive technical and organizational measures to protect your personal data including encryption, access controls, and regular security audits.</p>
                
                <h4>6. Data Retention</h4>
                <p>We retain your data only as long as necessary for business purposes and legal compliance, typically 7 years for financial records and 3 years for customer accounts.</p>
                
                <h4>7. Third-Party Sharing</h4>
                <p>We may share data with trusted service providers (payment processors, delivery services) and as required by law. We ensure adequate protection through contracts and security measures.</p>
                
                <h4>8. Contact Information</h4>
                <p>For privacy inquiries or to exercise your rights: <strong>greyroastery@gmail.com</strong></p>
                
                <h4>9. Compliance</h4>
                <p>This policy complies with Qatar's Personal Data Protection Law No. 13 of 2016 and international best practices for data protection.</p>
              </div>
            </div>
            
            <div className={styles.privacyCheckbox}>
              <input
                type="checkbox"
                id="privacyAccepted"
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                className={styles.checkbox}
                disabled={loading}
              />
              <label htmlFor="privacyAccepted" className={styles.privacyLabel}>
                I have read, understood, and agree to the Privacy Policy above and consent to the processing of my personal data in accordance with Qatar's Personal Data Protection Law No. 13 of 2016.*
              </label>
            </div>
          </div>

          {uploadStatus && (
            <div className={styles.uploadStatus}>
              {uploadStatus}
            </div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading || !privacyAccepted || !termsAccepted}
          >
            {loading ? (uploadStatus || 'Submitting...') : 'Submit Application'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default WholesaleApplication;